import { fontAttrs } from "@/components/tools/printables/page-kit";
import { INK, MUTED } from "@/components/tools/printables/sheet-header";
import type { Col, TablePage } from "@/lib/tool-kit/printables/attendance";
import { r2 } from "@/lib/tool-kit/printables/page";
import {
  type FontSpec,
  type TextMeasure,
  truncateToFit,
} from "@/lib/tool-kit/printables/text";

const SANS: FontSpec = { kind: "sans" };
const BOLD: FontSpec = { kind: "sans", bold: true };
const RULE = "#8a8a8a";
const BAND = "#e9e9e9";
const OFF = "#d6d6d6";

export interface TableColumn {
  col: Col;
  kind: "number" | "name" | "cell";
  /** First heading line (the weekday, "Name", "Signature"). */
  head?: string;
  /** Second heading line (the day number, the date). */
  sub?: string;
  /** Shade the column (a day off). */
  shade?: boolean;
}

/** A register table: heading band, numbered rows, names, and grid lines. */
export function AttendanceTable({
  page,
  columns,
  names,
  rowHeight,
  tableHeader,
  right,
  measure,
  firstRowNumber = 1,
}: {
  page: TablePage;
  columns: readonly TableColumn[];
  /** Names by row index; blank entries print as empty rows. */
  names: readonly string[];
  rowHeight: number;
  tableHeader: number;
  right: number;
  measure: TextMeasure;
  firstRowNumber?: number;
}) {
  const left = columns[0]?.col.x ?? 0;
  const lastRow = page.rows.at(-1);
  const bottom = lastRow ? lastRow.y + rowHeight : page.headerY + tableHeader;
  const nameSize = Math.min(3.8, rowHeight * 0.56);
  return (
    <g>
      <rect
        fill={BAND}
        height={r2(tableHeader)}
        width={r2(right - left)}
        x={r2(left)}
        y={r2(page.headerY)}
      />
      {columns.map((column) =>
        column.shade ? (
          <rect
            fill={OFF}
            height={r2(bottom - page.headerY)}
            key={`shade-${column.col.x}`}
            width={column.col.width}
            x={column.col.x}
            y={r2(page.headerY)}
          />
        ) : null
      )}
      {columns.map((column) => {
        const cx = r2(column.col.x + column.col.width / 2);
        const headX = column.kind === "name" ? r2(column.col.x + 2) : cx;
        const anchor = column.kind === "name" ? "start" : "middle";
        const twoLines = column.head !== undefined && column.sub !== undefined;
        return (
          <g fill={INK} key={`head-${column.col.x}`}>
            {column.head ? (
              <text
                fontSize={twoLines ? 2.9 : 3.4}
                textAnchor={anchor}
                x={headX}
                y={r2(
                  page.headerY +
                    (twoLines ? tableHeader * 0.4 : tableHeader * 0.62)
                )}
                {...fontAttrs(BOLD)}
              >
                {column.head}
              </text>
            ) : null}
            {column.sub ? (
              <text
                fontSize={3.2}
                textAnchor="middle"
                x={cx}
                y={r2(page.headerY + tableHeader * 0.82)}
                {...fontAttrs(SANS)}
              >
                {column.sub}
              </text>
            ) : null}
          </g>
        );
      })}
      {page.rows.map((row) => {
        const name = names[row.index] ?? "";
        const nameColumn = columns.find((column) => column.kind === "name");
        const numberColumn = columns.find((column) => column.kind === "number");
        return (
          <g key={row.index}>
            <line
              stroke={RULE}
              strokeWidth={0.25}
              x1={r2(left)}
              x2={r2(right)}
              y1={r2(row.y + rowHeight)}
              y2={r2(row.y + rowHeight)}
            />
            {numberColumn ? (
              <text
                fill={MUTED}
                fontSize={Math.min(3, rowHeight * 0.45)}
                textAnchor="middle"
                x={r2(numberColumn.col.x + numberColumn.col.width / 2)}
                y={r2(row.y + rowHeight * 0.66)}
                {...fontAttrs(SANS)}
              >
                {row.index + firstRowNumber}
              </text>
            ) : null}
            {name && nameColumn ? (
              <text
                fill={INK}
                fontSize={nameSize}
                x={r2(nameColumn.col.x + 2)}
                y={r2(row.y + rowHeight * 0.66)}
                {...fontAttrs(SANS)}
              >
                {truncateToFit(
                  measure,
                  name,
                  SANS,
                  nameSize,
                  nameColumn.col.width - 3
                )}
              </text>
            ) : null}
          </g>
        );
      })}
      {columns.map((column) => (
        <line
          key={`v-${column.col.x}`}
          stroke={RULE}
          strokeWidth={0.25}
          x1={column.col.x}
          x2={column.col.x}
          y1={r2(page.headerY)}
          y2={r2(bottom)}
        />
      ))}
      <line
        stroke={RULE}
        strokeWidth={0.25}
        x1={r2(right)}
        x2={r2(right)}
        y1={r2(page.headerY)}
        y2={r2(bottom)}
      />
      <line
        stroke={RULE}
        strokeWidth={0.25}
        x1={r2(left)}
        x2={r2(right)}
        y1={r2(page.headerY)}
        y2={r2(page.headerY)}
      />
    </g>
  );
}
